import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

// Helpers
const getProductId = (p) => p?._id || p?.id;
const getProductStock = (p) =>
  typeof p?.stock === 'number'
    ? p.stock
    : typeof p?.currentStock === 'number'
      ? p.currentStock
      : 0;

const formatCurrency = (value) => {
  const n = Number(value) || 0;
  return n.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2
  });
};

const SaleModal = ({
  isOpen,
  onClose,
  onConfirmSale,
  inventoryData = [],
  userRole = 'admin'
}) => {
  const [saleData, setSaleData] = useState({
    customer: '',
    customerType: 'retail',
    paymentMethod: 'cash',
    items: [],
    notes: '',
    discount: 0,
    tax: 16
  });

  const [searchQuery, setSearchQuery] = useState('');

  // 🔎 Productos con stock y búsqueda
  const availableProducts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!Array.isArray(inventoryData)) return [];

    const filtered = inventoryData.filter((item) => {
      const stock = getProductStock(item);
      if (stock <= 0) return false;
      if (!query) return true;

      const name = item.name?.toLowerCase() || '';
      const code = item.code?.toLowerCase() || '';
      const barcode = item.barcode?.toLowerCase() || '';
      return (
        name.includes(query) ||
        code.includes(query) ||
        barcode.includes(query)
      );
    });

    return filtered.slice(0, 15);
  }, [inventoryData, searchQuery]);

  // 🧮 Totales
  const totals = useMemo(() => {
    const subtotal = saleData.items.reduce(
      (sum, item) => sum + (Number(item.price) || 0) * item.quantity,
      0
    );

    const discountAmount = (subtotal * (Number(saleData.discount) || 0)) / 100;
    const taxable = subtotal - discountAmount;
    const taxAmount = (taxable * (Number(saleData.tax) || 0)) / 100;
    const total = taxable + taxAmount;

    const totalUnits = saleData.items.reduce(
      (sum, item) => sum + item.quantity,
      0
    );

    return {
      subtotal,
      discountAmount,
      taxAmount,
      total,
      totalUnits
    };
  }, [saleData.items, saleData.discount, saleData.tax]);

  // ➕ Agregar producto
  const handleAddProduct = useCallback((product) => {
    const id = getProductId(product);
    if (!id) return;

    setSaleData((prev) => {
      const existing = prev.items.find((i) => i.id === id);
      const stock = getProductStock(product);
      const price = Number(product.price) || 0;
      const cost = Number(product.cost) || 0;

      if (existing) {
        const newQty = Math.min(existing.quantity + 1, existing.maxQuantity);
        return {
          ...prev,
          items: prev.items.map((i) =>
            i.id === id ? { ...i, quantity: newQty } : i
          )
        };
      }

      return {
        ...prev,
        items: [
          ...prev.items,
          {
            id,
            code: product.code,
            name: product.name,
            price,
            cost,
            quantity: 1,
            currentStock: stock,
            maxQuantity: stock
          }
        ]
      };
    });

    setSearchQuery('');
  }, []);

  // 🔢 Actualizar cantidad
  const handleUpdateQuantity = useCallback((productId, quantity) => {
    setSaleData((prev) => {
      const product = prev.items.find((item) => item.id === productId);
      if (!product) return prev;

      const newQuantity = Math.max(
        0,
        Math.min(quantity, product.maxQuantity)
      );

      if (newQuantity === 0) {
        return {
          ...prev,
          items: prev.items.filter((item) => item.id !== productId)
        };
      }

      return {
        ...prev,
        items: prev.items.map((item) =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      };
    });
  }, []);

  // ❌ Eliminar producto
  const handleRemoveProduct = useCallback((productId) => {
    setSaleData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== productId)
    }));
  }, []);

  // ✅ Confirmar venta
  const handleConfirmSale = useCallback(() => {
    if (saleData.items.length === 0) return;

    const saleRecord = {
      ...saleData,
      id: `SALE-${Date.now()}`,
      date: new Date().toISOString(),
      status: 'completed',
      totals
    };

    onConfirmSale?.(saleRecord);

    setSaleData({
      customer: '',
      customerType: 'retail',
      paymentMethod: 'cash',
      items: [],
      notes: '',
      discount: 0,
      tax: 16
    });
    setSearchQuery('');
    onClose?.();
  }, [saleData, totals, onConfirmSale, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 p-0 safe-area-inset overflow-hidden">
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Contenedor principal - fondo gris oscuro neutro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 15 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="
            relative z-10 w-full h-full 
            bg-gray-900/95 
            border border-emerald-800/40 
            shadow-[0_18px_60px_rgba(15,23,42,0.9)]
            flex flex-col overflow-hidden
          "
        >
          {/* Header */}
          <div className="
            flex items-start md:items-center justify-between gap-3 
            px-4 md:px-6 py-4 
            border-b border-emerald-800/40 
            bg-gray-900
            pt-10 md:pt-4
          ">
            <div className="flex items-start gap-3 min-w-0">
              <div className="
                w-12 h-12 
                rounded-2xl 
                flex items-center justify-center 
                bg-gray-800 
                border border-emerald-500/70
              ">
                <Icon name="ShoppingCart" size={24} className="text-emerald-300" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-lg md:text-2xl font-semibold text-slate-50 truncate">
                    Registrar venta
                  </h1>
                  <span className="
                    inline-flex text-[11px] font-mono px-2 py-0.5 rounded-full 
                    bg-emerald-500/10 text-emerald-200 border border-emerald-400/60
                  ">
                    POS
                  </span>
                </div>
                <p className="text-xs md:text-sm flex items-center gap-2 text-slate-300">
                  <Icon
                    name="Navigation"
                    size={14}
                    className="flex-shrink-0 text-emerald-300"
                  />
                  <span className="truncate">
                    Selecciona productos, ajusta cantidades y confirma el cobro.
                  </span>
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <div className="hidden sm:flex flex-col items-end text-[11px] text-slate-300">
                <span>
                  Cliente:{' '}
                  <span className="text-slate-50 font-medium">
                    {saleData.customer || 'Mostrador'}
                  </span>
                </span>
                <span>
                  Total actual:{' '}
                  <span className="text-emerald-300 font-semibold">
                    {formatCurrency(totals.total)}
                  </span>
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-2 md:p-3 hover:bg-gray-800 rounded-xl transition-colors active:scale-95"
              >
                <Icon
                  name="X"
                  size={20}
                  className="text-slate-400 hover:text-slate-100"
                />
              </button>
            </div>
          </div>

          {/* Contenido */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:p-6 space-y-4 lg:space-y-6">
            {/* Datos del cliente */}
            <div className="
              bg-gray-900/90 
              border border-emerald-800/40 
              rounded-2xl 
              p-4 sm:p-5 
              shadow-inner shadow-black/40 
              space-y-3
            ">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <div className="
                    w-8 h-8 rounded-xl 
                    bg-gray-800 
                    flex items-center justify-center 
                    text-emerald-300 
                    border border-emerald-500/60
                  ">
                    <Icon name="User" className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-slate-50">
                      Datos del cliente
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Información básica para el ticket o factura.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <Input
                  label="Cliente"
                  value={saleData.customer}
                  onChange={(e) =>
                    setSaleData((prev) => ({ ...prev, customer: e.target.value }))
                  }
                  placeholder="Nombre del cliente o 'Mostrador'"
                  className="bg-gray-950/70 border-gray-800 text-slate-50"
                />
                <Select
                  label="Tipo de Cliente"
                  options={[
                    { value: 'retail', label: 'Venta al público' },
                    { value: 'wholesale', label: 'Mayorista' },
                    { value: 'regular', label: 'Cliente frecuente' }
                  ]}
                  value={saleData.customerType}
                  onChange={(value) =>
                    setSaleData((prev) => ({ ...prev, customerType: value }))
                  }
                />
                <Select
                  label="Método de pago"
                  options={[
                    { value: 'cash', label: 'Efectivo' },
                    { value: 'card', label: 'Tarjeta' },
                    { value: 'transfer', label: 'Transferencia' },
                    { value: 'credit', label: 'Crédito' }
                  ]}
                  value={saleData.paymentMethod}
                  onChange={(value) =>
                    setSaleData((prev) => ({ ...prev, paymentMethod: value }))
                  }
                />
              </div>
            </div>

            {/* Zona principal: Productos + Carrito */}
            <div className="grid gap-4 lg:grid-cols-2">
              {/* 🔍 Búsqueda de productos */}
              <div className="
                bg-gray-900/90 
                border border-emerald-800/40 
                rounded-2xl 
                p-4 sm:p-5 
                shadow-inner shadow-black/40 
                space-y-4
              ">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="
                      w-8 h-8 rounded-xl 
                      bg-gray-800 
                      flex items-center justify-center 
                      text-emerald-300 
                      border border-emerald-500/60
                    ">
                      <Icon name="Search" className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-slate-50">
                        Buscar productos
                      </h3>
                      <p className="text-[11px] text-slate-400">
                        Filtra por nombre, código o código de barras.
                      </p>
                    </div>
                  </div>
                  <span className="hidden sm:inline text-[11px] text-slate-400">
                    {availableProducts.length} resultado(s)
                  </span>
                </div>

                <Input
                  placeholder="Buscar productos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  iconName="Search"
                  className="bg-gray-950/70 border-gray-800 text-slate-50"
                />

                <div className="
                  mt-3 max-h-64 overflow-y-auto 
                  border border-emerald-900/40 
                  rounded-xl 
                  bg-gray-950/90
                ">
                  {availableProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-500 text-xs">
                      <Icon name="Package" size={32} className="mb-2 opacity-60 text-emerald-400" />
                      <p>No se encontraron productos con stock disponible.</p>
                    </div>
                  ) : (
                    availableProducts.map((product) => {
                      const stock = getProductStock(product);
                      const id = getProductId(product);

                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => handleAddProduct(product)}
                          className="
                            w-full flex items-center justify-between 
                            px-3 py-3 
                            border-b border-gray-900/70 last:border-b-0 
                            hover:bg-gray-800/70 
                            transition-colors text-left
                          "
                        >
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-slate-50">
                              {product.name}
                            </span>
                            <span className="text-[11px] text-slate-400">
                              Código: {product.code || '—'} • Stock:{' '}
                              <span className="text-emerald-300">{stock}</span> • Precio:{' '}
                              <span className="text-emerald-300">
                                {formatCurrency(product.price)}
                              </span>
                            </span>
                          </div>
                          <span className="
                            inline-flex items-center gap-1 text-[11px] 
                            text-emerald-200 
                            bg-gray-900 
                            border border-emerald-500/60 
                            px-3 py-1 rounded-full
                          ">
                            <Icon name="Plus" size={12} />
                            Agregar
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* 🛒 Carrito */}
              <div className="
                bg-gray-900/90 
                border border-emerald-800/40 
                rounded-2xl 
                p-4 sm:p-5 
                shadow-inner shadow-black/40 
                space-y-4
              ">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="
                      w-8 h-8 rounded-xl 
                      bg-gray-800 
                      flex items-center justify-center 
                      text-emerald-300 
                      border border-emerald-500/60
                    ">
                      <Icon name="ShoppingBag" className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-slate-50">
                        Carrito de venta
                      </h3>
                      <p className="text-[11px] text-slate-400">
                        Ajusta cantidades y revisa el total por producto.
                      </p>
                    </div>
                  </div>
                  <span className="text-[11px] text-slate-400">
                    {saleData.items.length} producto(s) • {totals.totalUnits} unidad(es)
                  </span>
                </div>

                {saleData.items.length === 0 ? (
                  <div className="
                    text-center py-8 text-slate-500 
                    border-2 border-dashed border-emerald-800/40 
                    rounded-xl
                  ">
                    <Icon
                      name="Package"
                      size={40}
                      className="mx-auto mb-3 opacity-70 text-emerald-400"
                    />
                    <p className="text-sm text-slate-100/90">
                      No hay productos en la venta.
                    </p>
                    <p className="text-xs text-slate-400">
                      Busca un producto y haz clic en &quot;Agregar&quot;.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {saleData.items.map((item) => (
                      <div
                        key={item.id}
                        className="
                          flex items-center justify-between 
                          p-3 
                          bg-gray-950/95 
                          rounded-xl 
                          border border-gray-800
                        "
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-50 truncate">
                              {item.name}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            Código: {item.code || '—'} • Precio:{' '}
                            <span className="text-emerald-300">
                              {formatCurrency(item.price)}
                            </span>{' '}
                            • Stock:{' '}
                            <span className="text-emerald-300">
                              {item.currentStock ?? item.maxQuantity}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleUpdateQuantity(item.id, item.quantity - 1)
                              }
                              disabled={item.quantity <= 1}
                              className="
                                w-8 h-8 p-0 
                                border-gray-700 
                                text-slate-200 
                                hover:bg-gray-900
                              "
                              iconName="Minus"
                              iconSize={12}
                            />
                            <span className="text-slate-50 font-semibold w-8 text-center text-sm">
                              {item.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleUpdateQuantity(item.id, item.quantity + 1)
                              }
                              disabled={item.quantity >= item.maxQuantity}
                              className="
                                w-8 h-8 p-0 
                                border-gray-700 
                                text-slate-200 
                                hover:bg-gray-900 
                                disabled:opacity-40
                              "
                              iconName="Plus"
                              iconSize={12}
                            />
                          </div>

                          <div className="text-right min-w-[88px]">
                            <div className="text-xs text-slate-400">Importe</div>
                            <div className="text-sm font-semibold text-emerald-300">
                              {formatCurrency(item.price * item.quantity)}
                            </div>
                          </div>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveProduct(item.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            iconName="Trash2"
                            iconSize={16}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notas + Resumen */}
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Notas & descuento */}
              <div className="
                bg-gray-900/90 
                border border-emerald-800/40 
                rounded-2xl 
                p-4 sm:p-5 
                shadow-inner shadow-black/40 
                space-y-4
              ">
                <div className="flex items-center gap-2 mb-1">
                  <div className="
                    w-8 h-8 rounded-xl 
                    bg-gray-800 
                    flex items-center justify-center 
                    text-emerald-200 
                    border border-emerald-500/60
                  ">
                    <Icon name="FileText" className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold text-slate-50">
                      Notas y ajustes
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Observaciones para el cliente o uso interno.
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <Input
                    label="Descuento (%)"
                    type="number"
                    value={saleData.discount}
                    onChange={(e) =>
                      setSaleData((prev) => ({
                        ...prev,
                        discount: Math.max(
                          0,
                          Math.min(100, Number(e.target.value) || 0)
                        )
                      }))
                    }
                    min="0"
                    max="100"
                    className="bg-gray-950/70 border-gray-800 text-slate-50"
                  />
                  <Input
                    label="IVA (%)"
                    type="number"
                    value={saleData.tax}
                    onChange={(e) =>
                      setSaleData((prev) => ({
                        ...prev,
                        tax: Math.max(0, Number(e.target.value) || 0)
                      }))
                    }
                    className="bg-gray-950/70 border-gray-800 text-slate-50"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-400 mb-1">
                    Notas de la venta
                  </label>
                  <textarea
                    rows={3}
                    value={saleData.notes}
                    onChange={(e) =>
                      setSaleData((prev) => ({ ...prev, notes: e.target.value }))
                    }
                    className="
                      w-full bg-gray-950/80 border border-gray-800 
                      rounded-xl text-sm text-slate-50 p-3 
                      focus:outline-none focus:ring-2 focus:ring-emerald-500/50 
                      resize-none
                    "
                    placeholder="Ejemplo: Se entregó sin caja, cliente acepta condiciones, etc."
                  />
                </div>
              </div>

              {/* Resumen financiero */}
              <div className="
                bg-gray-900/95 
                border border-emerald-800/60 
                rounded-2xl 
                p-4 sm:p-5 
                shadow-lg shadow-black/60 
                space-y-4
              ">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="
                      w-8 h-8 rounded-xl 
                      bg-gray-800 
                      border border-emerald-700/60 
                      flex items-center justify-center 
                      text-emerald-200
                    ">
                      <Icon name="Calculator" className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-slate-50">
                        Resumen de venta
                      </h3>
                      <p className="text-[11px] text-slate-400">
                        Subtotal, descuentos e impuestos.
                      </p>
                    </div>
                  </div>
                  <span className="text-[11px] text-slate-400">
                    {saleData.items.length} producto(s)
                  </span>
                </div>

                <div className="space-y-2 text-xs text-slate-200">
                  <div className="flex items-center justify-between">
                    <span>Subtotal</span>
                    <span className="font-mono">
                      {formatCurrency(totals.subtotal)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>
                      Descuento ({Number(saleData.discount) || 0}%)
                    </span>
                    <span className="font-mono text-amber-300">
                      -{formatCurrency(totals.discountAmount)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>
                      IVA ({Number(saleData.tax) || 0}%)
                    </span>
                    <span className="font-mono text-emerald-200">
                      {formatCurrency(totals.taxAmount)}
                    </span>
                  </div>
                  <div className="
                    border-t border-gray-800 pt-2 mt-1 
                    flex items-center justify-between
                  ">
                    <span className="font-semibold text-slate-50 text-sm">
                      Total a cobrar
                    </span>
                    <span className="font-mono text-xl text-emerald-300">
                      {formatCurrency(totals.total)}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Este es el monto final sugerido para cobrar al cliente con la
                    configuración actual.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="
            border-t border-emerald-800/40 
            bg-gray-900/95 
            px-3 sm:px-4 lg:px-6 py-3 lg:py-4
          ">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-wrap gap-2 text-[11px] sm:text-xs text-slate-200">
                <span>
                  Productos:{' '}
                  <span className="text-emerald-300 font-medium">
                    {saleData.items.length}
                  </span>
                </span>
                <span>
                  Unidades:{' '}
                  <span className="text-emerald-300 font-medium">
                    {totals.totalUnits}
                  </span>
                </span>
                <span>
                  Total:{' '}
                  <span className="text-emerald-300 font-semibold">
                    {formatCurrency(totals.total)}
                  </span>
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="
                    w-full sm:w-auto 
                    border-gray-700 
                    text-slate-300 
                    hover:bg-gray-800 hover:text-slate-100 
                    py-3 px-4 text-sm min-h-[44px]
                  "
                >
                  Cancelar
                </Button>
                <motion.div
                  className="w-full sm:w-auto"
                  whileHover={{ scale: saleData.items.length ? 1.02 : 1 }}
                  whileTap={{ scale: saleData.items.length ? 0.98 : 1 }}
                  transition={{ duration: 0.12 }}
                >
                  <Button
                    variant="default"
                    onClick={handleConfirmSale}
                    disabled={saleData.items.length === 0}
                    className="
                      w-full sm:w-auto 
                      bg-emerald-500 
                      hover:bg-emerald-600 
                      text-white 
                      shadow-lg shadow-emerald-500/40 
                      py-3 px-6 text-sm min-h-[44px] 
                      disabled:opacity-40 disabled:cursor-not-allowed
                    "
                    iconName="Check"
                    iconSize={16}
                  >
                    Confirmar venta
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SaleModal;
